import React, { useState } from 'react';
import axios from 'axios';
import { X, Loader2, Mail, Gift, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SAVINGS_BY_COUNTRY = {
  UK: { amount: '£7,200', competitor: 'Rightmove', benefit: 'unlimited listings and AI tools' },
  IN: { amount: '₹32,000', competitor: '99acres', benefit: 'unlimited Hindi/English listings and WhatsApp leads' },
  CA: { amount: 'C$9,852', competitor: 'Realtor.ca', benefit: 'unlimited listings for independent and brokerage agents' },
};

const LeadCaptureModal = ({ isOpen, onClose, country = 'UK', source = 'for-agents' }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const c = SAVINGS_BY_COUNTRY[country] || SAVINGS_BY_COUNTRY.UK;

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@') || !trimmed.includes('.')) {
      toast.error('Please enter a valid email');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/sales/capture-email`, {
        email: trimmed,
        country,
        source,
      });
      setDone(true);
      toast.success('📬 Savings breakdown sent to your inbox!');
      // Auto-close after 3 seconds
      setTimeout(() => onClose(), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong — please try again');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
      data-testid="lead-capture-modal"
    >
      <div
        className="bg-white max-w-md w-full relative shadow-2xl animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-slate-400 hover:text-[#E07A3A] transition-colors z-10"
          aria-label="Close"
          data-testid="lead-capture-close"
        >
          <X className="h-5 w-5" />
        </button>

        {done ? (
          /* Success state */
          <div className="p-10 text-center" data-testid="lead-capture-success">
            <div className="w-16 h-16 bg-[#25D366]/20 border border-[#25D366]/40 mx-auto flex items-center justify-center mb-5">
              <CheckCircle className="h-8 w-8 text-[#25D366]" />
            </div>
            <h3 className="font-['Playfair_Display'] text-2xl text-[#0F172A] mb-3">Check your inbox 📬</h3>
            <p className="text-slate-500 text-sm mb-6">
              We&apos;ve sent a tailored breakdown of typical savings vs <span className="font-semibold text-[#0F172A]">{c.competitor}</span> plus the Shopperden agent toolkit to <span className="font-mono text-[#0F172A]">{email}</span>.
            </p>
            <p className="text-slate-400 text-xs">Don&apos;t see it? Check your spam folder — or just sign up directly.</p>
          </div>
        ) : (
          /* Email capture state */
          <>
            {/* Header with savings badge */}
            <div className="bg-[#0F172A] p-6 relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(224,122,58,0.25),transparent_60%)] pointer-events-none" />
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#E07A3A]/20 border border-[#E07A3A]/40 text-[#E07A3A] text-[10px] tracking-[0.2em] uppercase font-bold mb-4">
                  <Gift className="h-3 w-3" />
                  One more thing
                </div>
                <h3 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-white leading-tight mb-2">
                  See how Shopperden
                  <br />
                  compares for you.
                </h3>
                <p className="text-white/60 text-sm">
                  Drop your email and we&apos;ll send a tailored breakdown &mdash; typical annual saving vs {c.competitor}, plus {c.benefit}. No spam, no card required.
                </p>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@agency.com"
                  required
                  autoFocus
                  className="w-full pl-10 pr-4 py-3.5 border border-slate-200 focus:border-[#E07A3A] focus:outline-none text-sm text-[#0F172A] placeholder:text-slate-300"
                  data-testid="lead-capture-email-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#E07A3A] hover:bg-[#C4652E] text-white py-3.5 tracking-widest uppercase font-bold text-sm transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60"
                data-testid="lead-capture-submit"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Sending...</span>
                ) : (
                  `Send me the breakdown`
                )}
              </button>

              <p className="text-[10px] text-slate-400 text-center tracking-wider uppercase">
                No obligation · Unsubscribe anytime · Privacy-first
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default LeadCaptureModal;
